import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useFeature, useLanguage, useStreamAgent, useViewer } from '@treams/treams-hooks';
import {
	DismissFeatureType,
	useDismissFeatures,
} from '@treams/treams-hooks/src/useDismissFeatures';
import { useFeatureNeedsViewerApproval } from '@treams/treams-hooks/src/useFeatureNeedsViewerApproval';
import * as graphql from '@treams/treams-network';

import { EvaluationSubmissionAIInsightsApproval } from '../components/EvaluationSubmissionAIInsightsApproval';
import { AIInsightsContext } from './AIInsightsContext';

export function StreamingAIInsightsContextProvider(
	props: PropsWithChildren<{ submission: graphql.EvaluationSubmissionFragment }>,
) {
	const viewer = useViewer();

	const { isFeatureDismissed } = useDismissFeatures();

	const conversationAIInsightsEnabled = useFeature(graphql.FeatureName.ConversationAiInsights);

	const { needsViewerApproval: needsAIInsightsApproval, refetch: refetchNeedsViewerApproval } =
		useFeatureNeedsViewerApproval(graphql.FeatureName.ConversationAiInsights);

	const [showAIInsightsApprovalModal, setShowAIInsightsApprovalModal] = useState(false);
	const [featureIsApproved, setFeatureIsApproved] = useState(false);

	const viewerIsEmployee = viewer?.id === props.submission.employee.user.id;
	const aiInsightsApproved =
		props.submission.aiInsightsApproved || (viewerIsEmployee && !needsAIInsightsApproval);

	const language = useLanguage();
	const { stream, text: streamText } = useStreamAgent();
	const [streamStarted, setStreamStarted] = useState(false);
	const [streamFailed, setStreamFailed] = useState(false);

	const evaluationSubmissionCanShowAIInsights = conversationAIInsightsEnabled && aiInsightsApproved;

	const nonQuestionTypes = [
		graphql.EvaluationSectionItemType.Title,
		graphql.EvaluationSectionItemType.Paragraph,
		graphql.EvaluationSectionItemType.FinalScore,
		graphql.EvaluationSectionItemType.Signature,
	];

	const flatItemIds = useMemo(
		() =>
			props.submission.evaluationForm.sections.flatMap((section) =>
				section.items
					.filter((item) => !nonQuestionTypes.includes(item.type))
					.map((item) => item.id),
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.submission.evaluationForm.sections],
	);

	useEffect(() => {
		if (!evaluationSubmissionCanShowAIInsights) return;

		const controller = new AbortController();
		setStreamStarted(true);
		setStreamFailed(false);

		stream(
			viewerIsEmployee ? 'CONVERSATION_INSIGHTS_EMPLOYEE' : 'CONVERSATION_INSIGHTS_MANAGER',
			{
				evaluationSubmissionID: props.submission.id,
				subjectUserID: props.submission.employee.user.id,
				locale: language.id,
			},
			controller.signal,
		).catch((err) => {
			if (err instanceof DOMException && err.name === 'AbortError') return;
			setStreamFailed(true);
			setStreamStarted(false);
		});

		return () => controller.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [evaluationSubmissionCanShowAIInsights]);

	// In order to split the AI response for each question, I use a level-4 markdown heading
	// This is also explicitly mentioned in the AI prompt instructions
	const streamingInsightsData = useMemo(() => {
		if (!streamText) return {};

		const chunks = streamText.split(/####[^\n]+/g).slice(1);
		const record: Record<string, string> = {};

		chunks.forEach((chunk, index) => {
			if (index < flatItemIds.length) {
				record[flatItemIds[index]] = chunk.trim();
			}
		});

		return record;
	}, [streamText, flatItemIds]);

	const loadingInsights =
		streamStarted && !streamFailed && Object.keys(streamingInsightsData).length === 0;

	useEffect(() => {
		if (
			!viewerIsEmployee ||
			!conversationAIInsightsEnabled ||
			!props.submission.viewer.viewerCanUpdate ||
			!needsAIInsightsApproval ||
			isFeatureDismissed(
				DismissFeatureType.FEATURE_APPROVAL_MODAL,
				graphql.FeatureName.ConversationAiInsights,
			) ||
			featureIsApproved
		) {
			return;
		}

		setShowAIInsightsApprovalModal(true);
	}, [
		viewerIsEmployee,
		conversationAIInsightsEnabled,
		props.submission.viewer.viewerCanUpdate,
		needsAIInsightsApproval,
		props.submission,
		isFeatureDismissed,
		featureIsApproved,
	]);

	const successCallBack = () => {
		setFeatureIsApproved(true);
		refetchNeedsViewerApproval();
	};

	return (
		<AIInsightsContext.Provider
			value={{
				aiInsightsEnabled: conversationAIInsightsEnabled,
				aiInsightsApproved,
				needsViewerApproval: viewerIsEmployee && !aiInsightsApproved,
				refetchNeedsAIInsightsApproval: refetchNeedsViewerApproval,
				openAIInsightsApprovalModal: () => setShowAIInsightsApprovalModal(true),
				insightsLoading: loadingInsights,
				showAIInsightsFeedback: evaluationSubmissionCanShowAIInsights,
				streamingInsightsData,
			}}
		>
			{props.children}
			{showAIInsightsApprovalModal && (
				<EvaluationSubmissionAIInsightsApproval
					submission={props.submission as graphql.EvaluationSubmission}
					onDismiss={() => setShowAIInsightsApprovalModal(false)}
					successCallBack={successCallBack}
				/>
			)}
		</AIInsightsContext.Provider>
	);
}
